import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { openStoredDocument } from '@/lib/openDocument.js';
import { UPDATE_INTEREST_EXPECTED, updateInterest, uploadInterestCert } from '@/services/interests.service.js';
import { announceDocumentsChanged, announceLedgerChanged, fileDealDocuments } from '@/services/deals.service.js';
import { confirmDocumentForSend } from '@/services/documentSends.service.js';

// The pieces both send sheets share (paperwork-1 screen 3, L3-2): a document line (derived name, original filename, size,
// earlier sends), a send line read back from document_sends, and the button that resolves each missing item. Nothing here
// derives who gets what: the server's preview says it (utils/standard-set.js), the sheet renders it.

export function sizeWord(n) {
  if (!Number.isFinite(n)) return 'size read when sent';
  if (n < 1024) return `${n} bytes`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Long names wrap inside a button instead of pushing the sheet wider than a phone.
const WRAP = 'h-auto min-h-8 whitespace-normal py-1 text-left';
const day = (v) => (v ? String(v).slice(0, 10) : null);

/** What one document_sends row says, in words: delivered, where an override send went, a failure, an old carried send. */
export function sendWord(s) {
  if (!s) return 'Not checked';
  if (s.status === 'unchecked') return 'Sent; could not read it back';
  if (s.delivered) return `Delivered to ${s.actual_to || s.intended_to}${s.sent_at ? ` on ${day(s.sent_at)}` : ''}`;
  if (s.label) return s.label;
  if (s.status === 'sent' && s.override_active) return `Went to the test address ${s.actual_to}, not to ${s.intended_to}`;
  if (s.status === 'suppressed') return 'Held by rehearsal mode, not sent';
  if (s.status === 'failed') return `Failed: ${s.error || 'the mail did not go'}`;
  if (s.status === 'claimed') return 'May not have gone (no answer recorded)';
  return s.status;
}

/** The sends listed as "Earlier" under a document: every one but the send(s) the result just reported (second Chrome pass). */
export function earlierSends(sends, result) {
  const now = new Set([...(result?.sends || []), ...(result?.send ? [result.send] : [])].map((s) => s && String(s.id)));
  return (sends || []).filter((s) => !now.has(String(s.id)));
}

export function DocumentLine({ doc, note }) {
  return (
    <li className="rounded-card border border-border px-3 py-2 text-[13px]" data-send-document={doc.document_id || doc.bundle_inbound_document_id || doc.key}>
      <div className="font-medium text-text">{doc.name || doc.original_filename || 'Document'}</div>
      <div className="mt-0.5 text-[11px] text-text-dim">
        {doc.original_filename ? <>Your file: {doc.original_filename} · </> : null}
        {sizeWord(doc.byte_size)}
      </div>
      {(doc.earlier_sends || []).map((s) => (
        <div key={s.id} className="mt-0.5 text-[11px] text-text-muted">Earlier: {sendWord(s)}</div>
      ))}
      {note ? <div className="mt-0.5 text-[11px] text-text-muted">{note}</div> : null}
    </li>
  );
}

const MISSING_WORDS = {
  no_address: 'has no email address of his own on his record',
  not_an_address: 'has an email address that is not one',
  could_not_check: 'could not be checked (the record did not answer)',
  no_deal_contact: 'is not named on the deal (no contact on the Deal)',
  no_email: 'has no email on file',
  no_certificate: 'has no certificate on file',
  no_holder_document: 'has no certificate or loss-payee endorsement on file',
  unconfirmed: 'is not confirmed as this policy\'s yet, so it is never sent',
  too_large: 'is too large to attach to an email',
  no_approved_bundle: 'is not approved yet (no reviewed NDEMNIFLY binder)',
  stale_bundle: 'is out of date: regenerate before sending',
  no_carrier_document: 'is not attached to this endorsement yet',
};
export const missingWord = (reason) => MISSING_WORDS[reason] || reason;

// "Add others (CC)" (round 2, Q21: every send sheet): free addresses, typed, sent as they are; the server validates them
// (400 BAD_CC) and copies them on the email named by `to`. The automatic send has no such field.
export const ccList = (text) => String(text || '').split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
export function CcField({ value, onChange, to, disabled }) {
  return (
    <label className="block text-[12px] text-text-muted" data-cc-field>
      <span className="font-medium text-text">Add others (CC)</span>: anyone else who should get a copy of the email to {to}. Separate addresses with commas.
      <input type="text" inputMode="email" autoComplete="off" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
        placeholder="name@example.com, another@example.com" aria-label={`Add others (CC) on the email to ${to}`}
        className="mt-1 min-h-[36px] w-full rounded-card border border-border bg-bg px-2 text-sm text-text" data-cc-input />
    </label>
  );
}
/**
 * Who was copied, from the server's answer (the transport drops every CC while the test address override is on). A CC travels
 * only on a client mail that went: when none did, the server's cc_not_sent says who was NOT copied and why (review fix, round 2).
 */
export function CcResult({ out, to }) {
  const on = to ? ` on the email to ${to}` : '';
  if (out?.cc_not_sent?.addresses?.length) return <p className="text-[12px] text-warning" data-cc-result data-cc-not-sent>Not copied, {out.cc_not_sent.reason}: {out.cc_not_sent.addresses.join(', ')}</p>;
  if (!out || !Array.isArray(out.cc) || !out.cc.length) return null;
  return <p className="text-[12px] text-text-muted" data-cc-result>{out.cc_dropped_by_override ? `Not copied${on}, the test address override is on: ${out.cc.join(', ')}` : `Copied${on}: ${out.cc.join(', ')}`}</p>;
}

/**
 * The button that resolves one missing item (ui-screens: every to-do carries its button). `onDone` re-reads the preview.
 * ctx: { dealId, client: { label, crm_account_id }, onReviewPaperwork, onRegenerateBinder?, regeneratingBinder? }
 */
export function MissingItem({ item, ctx, onDone }) {
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(null);
  const fileRef = useRef(null);
  const endtRef = useRef(null);
  const [kind, id] = String(item.action || '').split(':');
  const who = item.name || (item.recipient && item.recipient.label) || 'this document';
  const run = async (fn, ok) => { setBusy(true); try { await fn(); if (ok) toast.success(ok); onDone && onDone(); } catch (e) { toast.error(e?.body?.error || e?.message || 'That did not work'); } finally { setBusy(false); } };

  // stack: a line with several controls (the holder's email, its Save and "forwards it") keeps its words on their own row above
  // the controls, which wrap (the Chrome pass of 2026-11-02 found them squeezing the words to one word a line).
  const line = (text, control, { stack = false } = {}) => (
    <li className={`flex flex-col gap-1.5 rounded-card border border-warning/40 bg-warning-soft/30 px-3 py-2 text-[13px]${stack ? '' : ' sm:flex-row sm:items-center sm:justify-between'}`} data-missing={item.action}>
      <span className="text-text">{text}</span>
      <span className={`flex flex-wrap items-center gap-2${stack ? '' : ' shrink-0'}`}>{control}</span>
    </li>
  );

  if (kind === 'edit_client_email') {
    const label = ctx.client?.label || 'the client';
    return line(`${label} ${missingWord(item.reason)}.`, ctx.client?.crm_account_id
      ? <Button asChild size="sm" variant="outline" className={WRAP}><Link to={`/account/${encodeURIComponent(ctx.client.crm_account_id)}`}>Open {label}'s record</Link></Button>
      : <span className="text-[11px] text-text-dim">Name the client on the Deal first</span>);
  }
  if (kind === 'review_paperwork') {
    // A stale binder's button regenerates, then opens the review (David 2026-11-01: "The button does what it says").
    const regen = item.reason === 'stale_bundle' && ctx.onRegenerateBinder;
    return line(`The NDEMNIFLY binder ${missingWord(item.reason)}.`, regen
      ? <Button size="sm" variant="outline" className={WRAP} disabled={!!ctx.regeneratingBinder} onClick={() => ctx.onRegenerateBinder()} data-regenerate-binder-line>{ctx.regeneratingBinder ? 'Regenerating the binder...' : 'Regenerate the binder'}</Button>
      : <Button size="sm" variant="outline" className={WRAP} onClick={() => ctx.onReviewPaperwork && ctx.onReviewPaperwork()}>Review paperwork</Button>);
  }
  if (kind === 'confirm_document') {
    return line(`${who} ${missingWord('unconfirmed')}.`, <>
      <Button size="sm" variant="ghost" onClick={() => openStoredDocument(id)}>Open it</Button>
      <Button size="sm" variant="outline" className={WRAP} disabled={busy} onClick={() => run(() => confirmDocumentForSend(id), `${who} confirmed as this policy's`)}>Confirm it is this policy's</Button>
    </>);
  }
  if (kind === 'edit_interest_email') {
    // David 2026-11-02: a holder with no email is asked both ways on its line. The email is saved through the interests door
    // (logged) and the certificate then goes straight to the holder; "Client forwards it" is the broker's choice, never a default.
    const offered = item.forward_offered === true && typeof ctx.onForward === 'function';
    // A value that is not an address is refused by the door (400 BAD_EMAIL); its sentence shows on this line, where it was typed.
    const saveEmail = async () => {
      setBusy(true); setEmailError(null);
      try { await updateInterest(id, { email: email.trim(), crm_deal_id: ctx.dealId }, { expected: UPDATE_INTEREST_EXPECTED }); toast.success(`Email saved for ${who}`); onDone && onDone(); }
      catch (e) { if (e?.body?.code === 'BAD_EMAIL') setEmailError(e.body.error); else toast.error(e?.body?.error || e?.message || 'That did not work'); }
      finally { setBusy(false); }
    };
    return line(<>{who} {missingWord(item.reason)}.{offered ? <span className="block text-[12px] text-text-muted">Add the email to send it straight to {who}, or have {ctx.client?.label || 'the client'} forward it. The set waits for one.</span> : null}</>, <>
      <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setEmailError(null); }} placeholder={`Email for ${who}`} className="min-h-[32px] w-56 max-w-full rounded-card border border-border bg-bg px-2 text-sm" aria-label={`Email for ${who}`} aria-invalid={emailError ? true : undefined} data-holder-email={id} />
      <Button size="sm" variant="outline" className={WRAP} disabled={busy || !email.trim()} onClick={saveEmail} data-save-holder-email={id}>Save email for {who}</Button>
      {offered ? <Button size="sm" variant="ghost" className={WRAP} disabled={busy} onClick={() => ctx.onForward(id)} data-forward-holder={id}>{ctx.client?.label || 'Client'} forwards it</Button> : null}
      {emailError ? <span role="alert" className="block w-full text-[12px] text-danger" data-holder-email-error={id}>{emailError}</span> : null}
    </>, { stack: offered });
  }
  if (kind === 'drop_certificate') {
    const pick = async (e) => {
      const file = e.target.files?.[0]; e.target.value = '';
      if (!file) return;
      await run(async () => {
        const r = await uploadInterestCert(id, ctx.dealId, file);
        if (r.ask) throw new Error(`A certificate is already filed for ${who}; use the interests panel to say whether this replaces it.`);
        announceDocumentsChanged(ctx.dealId);
      }, `Certificate filed for ${who}`);
    };
    return line(`${who} ${missingWord('no_certificate')}.`, <>
      <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={pick} />
      <Button size="sm" variant="outline" className={WRAP} disabled={busy} onClick={() => fileRef.current?.click()}>Drop the certificate for {who}</Button>
    </>);
  }
  if (kind === 'drop_holder_document') {
    // Q19 (David 2026-10-31): a lienholder or loss payee gets every document naming it; either one satisfies it.
    const pickCert = async (e) => {
      const file = e.target.files?.[0]; e.target.value = '';
      if (!file) return;
      await run(async () => {
        const r = await uploadInterestCert(id, ctx.dealId, file);
        if (r.ask) throw new Error(`A certificate is already filed for ${who}; use the interests panel to say whether this replaces it.`);
        announceDocumentsChanged(ctx.dealId);
      }, `Certificate filed for ${who}`);
    };
    const pickEndt = async (e) => {
      const file = e.target.files?.[0]; e.target.value = '';
      if (!file) return;
      await run(async () => {
        const body = await fileDealDocuments(ctx.dealId, [{ file, kind: 'endorsement', interest_id: id }]);
        const r = body.results[0] || {};
        if (!r.ok) throw new Error(r.error || 'Not filed');
        announceDocumentsChanged(ctx.dealId);
      }, `Loss-payee endorsement filed for ${who}`);
    };
    return line(`${who} ${missingWord('no_holder_document')}.`, <>
      <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={pickCert} />
      <input ref={endtRef} type="file" accept="application/pdf" className="hidden" onChange={pickEndt} />
      <Button size="sm" variant="outline" className={WRAP} disabled={busy} onClick={() => fileRef.current?.click()} data-drop-holder="certificate">Drop the certificate for {who}</Button>
      <Button size="sm" variant="outline" className={WRAP} disabled={busy} onClick={() => endtRef.current?.click()} data-drop-holder="endorsement">Drop the loss-payee endorsement for {who}</Button>
    </>);
  }
  if (kind === 'attach_carrier_document') {
    const pick = async (e) => {
      const file = e.target.files?.[0]; e.target.value = '';
      if (!file) return;
      await run(async () => {
        const body = await fileDealDocuments(ctx.dealId, [{ file, kind: 'endorsement', transaction_id: id }]);
        const r = body.results[0] || {};
        if (!r.ok) throw new Error(r.error || 'Not filed');
        announceDocumentsChanged(ctx.dealId); announceLedgerChanged(ctx.dealId);
      }, 'The carrier\'s endorsement is attached');
    };
    return line(`The carrier's endorsement ${missingWord('no_carrier_document')}.`, <>
      <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={pick} />
      <Button size="sm" variant="outline" className={WRAP} disabled={busy} onClick={() => fileRef.current?.click()}>Attach the carrier's endorsement</Button>
    </>);
  }
  if (kind === 'too_large') {
    return line(`${who} ${missingWord('too_large')}; send it another way.`, item.document_id ? <Button size="sm" variant="outline" className={WRAP} onClick={() => openStoredDocument(item.document_id)}>Open {who}</Button> : null);
  }
  return line(`${who}: ${missingWord(item.reason)}`, null);
}
